import { createEvent } from "effector";
import Cookies from "js-cookie";
import { resolveLanguageCode } from "i18n/resolve-language";
import { UNRELEASED_LANGUAGES } from "i18n/unreleased";

/**
 * Content locales supported by the backend (lessons/courses/roadmaps translations).
 * Independent from the UI i18n catalog: the backend serves these even if the UI
 * picker currently exposes a smaller subset. "en" is the default (no param sent).
 * Codes must match the `locale` column the admin writes — hence "pt_br", not "pt".
 */
export const CONTENT_LOCALES = ["en", "es", "fr", "de", "it", "ja", "pt_br", "ar"] as const;
export type ContentLocale = (typeof CONTENT_LOCALES)[number];

// Must match LANGUAGE_COOKIE in i18n/config.ts. Inlined here to keep this module
// free of the i18n bundle (and react-i18next), so API files stay SSR-safe.
const LANGUAGE_COOKIE = "i18nextLng";

// Same resolution as the i18n cookie ("pt-BR" and a plain "pt" both reach "pt_br",
// "ar-EG" reaches "ar"), so the content never lands in another language than the UI.
const normalize = (value?: string | null): ContentLocale | null => resolveLanguageCode(value, CONTENT_LOCALES);

/** Map any language tag to a content locale, falling back to "en". */
export const toContentLocale = (value?: string | null): ContentLocale => normalize(value) ?? "en";

/**
 * Fired when the user switches the language and the content locale actually
 * changes. Models holding localized content (roadmaps, courses, …) refetch on
 * it — otherwise cached English data stays on screen until a full reload.
 * Emitted from Providers (scope-bound), see app/Providers.tsx.
 */
export const contentLocaleChanged = createEvent<ContentLocale>();

/**
 * Resolve the current content locale from the language picker.
 * Order: i18next cookie -> navigator -> "en". Server-side returns "en".
 */
export const getContentLocale = (): ContentLocale => {
    if (typeof window === "undefined") return "en";
    const fromCookie = normalize(Cookies.get(LANGUAGE_COOKIE));
    if (fromCookie) return fromCookie;
    // Same rule as the UI's detectInitialLanguage: a locale still under test is
    // never taken from the browser, or the content would switch while the UI
    // stays English.
    const fromNavigator = normalize(window.navigator?.language);
    if (fromNavigator && !UNRELEASED_LANGUAGES.includes(fromNavigator)) return fromNavigator;
    return "en";
};

// QA/staff toggle: when set, content endpoints return unpublished AI drafts
// overlaid on top of translations/base content. Set from DevTools:
//   localStorage.setItem("content_preview", "true")
const CONTENT_PREVIEW_KEY = "content_preview";

export const isContentPreview = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(CONTENT_PREVIEW_KEY) === "true";
    } catch {
        return false;
    }
};

/**
 * Append the content query params to a URL: `locale` from the language picker
 * (skipped for English — the backend defaults to English) and `preview=1`
 * when the draft-preview localStorage flag is on.
 */
export const withContentParams = (url: string): string => {
    let out = url;
    const locale = getContentLocale();
    if (locale !== "en") out = appendParam(out, `locale=${locale}`);
    if (isContentPreview()) out = appendParam(out, "preview=1");
    return out;
};

/** @deprecated alias — use withContentParams; kept so call sites need no churn. */
export const withLocale = withContentParams;

const appendParam = (url: string, param: string): string => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${param}`;
};
